/**
 * Row Overlay Utility
 * Global utility for showing loading/success/error overlays on table rows
 *
 * Usage:
 *   RowOverlay.show($row, 'Loading...', 'loading');
 *   RowOverlay.update($row, 'Success!', 'success');
 *   RowOverlay.hide($row);
 *
 * States: 'loading', 'success', 'error'
 */

(function(window, $) {
  'use strict';

  const RowOverlay = {
    /**
     * Show an overlay on a table row
     * @param {jQuery} $row - The table row element
     * @param {string} message - The message to display
     * @param {string} type - The overlay type: 'loading', 'success', or 'error'
     */
    show ($row, message, type = 'loading') {
      if (!$row || !$row.length) {
        console.warn('RowOverlay.show: Invalid row element');
        return;
      }

      // Disable all buttons in the row
      $row.find('button, a.button').prop('disabled', true).css('pointer-events', 'none');

      // Get or create overlay container
      // Find the nearest positioned parent (card-body, table-wrapper, etc.)
      const $container = $row.closest('.card-body, .table-wrapper, .table-container').length
        ? $row.closest('.card-body, .table-wrapper, .table-container')
        : $row.closest('table').parent();

      if (!$container.length) {
        console.warn('RowOverlay.show: Could not find container for overlay');
        return;
      }

      // Ensure container is positioned
      if ($container.css('position') === 'static') {
        $container.css('position', 'relative');
      }

      // Store overlay reference on the row
      let $overlay = $row.data('row-overlay-element');

      if (!$overlay || !$overlay.length || !$.contains(document, $overlay[0])) {
        $overlay = $('<div class="row-overlay"></div>');
        $overlay.attr('data-row-id', $row.index());
        $container.append($overlay);
        $row.data('row-overlay-element', $overlay);

        // Position the overlay to cover the entire row
        const positionOverlay = () => {
          // Only position if overlay is still in DOM and active
          if (!$overlay.hasClass('active') || !$.contains(document, $overlay[0])) return;

          const rowRect = $row[0].getBoundingClientRect();
          const containerRect = $container[0].getBoundingClientRect();

          $overlay.css({
            position: 'absolute',
            top: (rowRect.top - containerRect.top + $container.scrollTop()) + 'px',
            left: (rowRect.left - containerRect.left + $container.scrollLeft()) + 'px',
            width: rowRect.width + 'px',
            height: rowRect.height + 'px',
          });
        };

        positionOverlay();

        // Store position handler
        $overlay.data('position-handler', positionOverlay);

        // Reposition on window resize
        const resizeHandler = () => positionOverlay();
        $(window).on('resize.row-overlay-' + $row.index(), resizeHandler);
        $overlay.data('resize-handler', resizeHandler);

        // Reposition on scroll
        const scrollHandler = () => positionOverlay();
        $(window).on('scroll.row-overlay-' + $row.index(), scrollHandler);
        $overlay.data('scroll-handler', scrollHandler);

        // Continuous repositioning using requestAnimationFrame for smooth updates
        let rafId = null;
        const rafUpdate = () => {
          positionOverlay();
          if ($overlay.hasClass('active')) {
            rafId = requestAnimationFrame(rafUpdate);
          }
        };
        rafId = requestAnimationFrame(rafUpdate);
        $overlay.data('raf-id', rafId);
      }

      // Set icon based on type
      const iconMap = {
        loading: 'fa-spinner fa-pulse',
        success: 'fa-check-circle',
        error: 'fa-exclamation-circle',
      };
      const icon = iconMap[type] || iconMap.loading;

      $overlay.html(`
        <div class="row-overlay-content ${type}">
          <i class="fas ${icon}"></i>
          <span>${message}</span>
        </div>
      `);

      $overlay.addClass('active');

      // Trigger an immediate reposition
      const positionHandler = $overlay.data('position-handler');
      if (positionHandler) {
        setTimeout(positionHandler, 0);
      }
    },

    /**
     * Update an existing overlay's message and type
     * @param {jQuery} $row - The table row element
     * @param {string} message - The new message to display
     * @param {string} type - The new overlay type: 'success' or 'error'
     */
    update ($row, message, type = 'success') {
      if (!$row || !$row.length) {
        console.warn('RowOverlay.update: Invalid row element');
        return;
      }

      const $overlay = $row.data('row-overlay-element');
      if (!$overlay || !$overlay.length) {
        console.warn('RowOverlay.update: No overlay found for row');
        return;
      }

      const iconMap = {
        success: 'fa-check-circle',
        error: 'fa-exclamation-circle',
      };
      const icon = iconMap[type] || iconMap.success;

      $overlay.find('.row-overlay-content')
        .removeClass('loading success error')
        .addClass(type)
        .html(`
          <i class="fas ${icon}"></i>
          <span>${message}</span>
        `);
    },

    /**
     * Hide and remove the overlay from a table row
     * @param {jQuery} $row - The table row element
     */
    hide ($row) {
      if (!$row || !$row.length) {
        console.warn('RowOverlay.hide: Invalid row element');
        return;
      }

      const $overlay = $row.data('row-overlay-element');
      if ($overlay && $overlay.length) {
        $overlay.removeClass('active');

        // Cancel requestAnimationFrame
        const rafId = $overlay.data('raf-id');
        if (rafId) {
          cancelAnimationFrame(rafId);
        }

        setTimeout(() => {
          $overlay.remove();

          const rowIndex = $row.index();

          // Clean up resize handler
          const resizeHandler = $overlay.data('resize-handler');
          if (resizeHandler) {
            $(window).off('resize.row-overlay-' + rowIndex, resizeHandler);
          }

          // Clean up scroll handler
          const scrollHandler = $overlay.data('scroll-handler');
          if (scrollHandler) {
            $(window).off('scroll.row-overlay-' + rowIndex, scrollHandler);
          }

          // Clear stored reference
          $row.removeData('row-overlay-element');
        }, 300);
      }

      // Re-enable buttons in the row
      $row.find('button, a.button').prop('disabled', false).css('pointer-events', '');
    },
  };

  // Expose globally
  window.RowOverlay = RowOverlay;

  // Also expose legacy names for backwards compatibility
  window.showRowOverlay = function($row, message, type) {
    RowOverlay.show($row, message, type);
  };

  window.updateRowOverlay = function($row, message, type) {
    RowOverlay.update($row, message, type);
  };

  window.hideRowOverlay = function($row) {
    RowOverlay.hide($row);
  };

})(window, jQuery);
